import * as React from 'react';
import moment from 'moment';
import { shallowEqual } from 'react-redux';
import { Col, Container, OverlayTrigger, Popover, Row, Table } from 'react-bootstrap';

import { TrialsDemographicsTable } from '../Components/TrialsDemographicsTable';
import { Ats2019AcceptabilityIcon } from '../../../components/Utilities/Ats2019AcceptabilityIcon';
import { OscillometryZScoreChart } from '../Charts/OscillometryZScoreChart';
import { MeasurementTrialLabelsDropdown } from '../../../components/Charts/MeasurementTrialLabelsDropdown';
import { OscillometryWaveformCard } from '../Components/OscillometryWaveformCard';
import { FrequencyCurveChart } from '../../../components/Charts/Osc/FrequencyCurveChart';
import FrequencyCurveModal from '../../../components/Charts/Osc/FrequencyCurveModal';
import OscillometryNotes from '../Components/OscillometryNotes';

import { useAppSelector } from '../../../redux/store';
import { OrganizationType, PatientDemographics, Program } from '../../../Types/Common/Types';
import { OscillometryMeasurement } from '../../../Types/Common/MeasurementTypes';
import { ACCEPTABLE, USABLE, NOT_ACCEPTABLE } from '../../../libs/dataLib';
import { isEmptyObjectOrArray } from '../../../libs/utilsLib';
import { GroupedOscillometryMeasurement } from './OscillometryDetailedView';
import { PatientID } from '../../../Types/Common/BrandedTypes';

const { useState } = React;

interface OscillometryDetailedFullViewProps {
  groupedMeasurement: GroupedOscillometryMeasurement;
  sessionDemographics: PatientDemographics | undefined;
  acceptabilityStatus: string;
  getRepeatabilityStatus: () => string;
  refetchMeasurements: () => void | Promise<void>;
  toggleRejected: (waveform: OscillometryMeasurement) => Promise<void>;
  saveAcceptabilityCriteria: (waveform: OscillometryMeasurement, criteria: Partial<Record<string, boolean>>) => Promise<void>;
  organization: OrganizationType;
  program: Program;
  isReadOnly: boolean | undefined;
  participantID: PatientID | undefined;
}

export const OscillometryDetailedFullView: React.FC<OscillometryDetailedFullViewProps> = ({
  groupedMeasurement,
  sessionDemographics,
  acceptabilityStatus,
  getRepeatabilityStatus,
  refetchMeasurements,
  toggleRejected,
  saveAcceptabilityCriteria,
  organization,
  program,
  isReadOnly,
  participantID
}) => {
  const translationText = useAppSelector(state => state.translations, shallowEqual);
  const authenticatedProvider = useAppSelector(state => state.provider, shallowEqual);
  const [showModal, setShowModal] = useState(false);

  const bestMeasurements = groupedMeasurement.bestMeasurements;
  const waveforms = groupedMeasurement.waveforms;
  const shouldShowVisitLabels = (program?.trialMode?.labels && !isEmptyObjectOrArray(program?.trialMode?.labels)) || program?.visitTemplate;
  const seriesEditing = program?.featureSet?.enableSeriesEditing;

  // Acceptability popover content
  const acceptablePopover = (description: string) => (
    <Popover id="acceptable-popover">
      <Popover.Title>
        {description === ACCEPTABLE
          ? translationText.acceptable || "Acceptable"
          : description === USABLE
            ? translationText.usable || "Usable"
            : description === NOT_ACCEPTABLE
              ? translationText.notAcceptable || "Not acceptable"
              : 'N/A'}
      </Popover.Title>
      <Popover.Content>
        {description === ACCEPTABLE
          ? translationText.oscillometryIsAcceptableBecause || "A session that has at least 3 acceptable manoeuvres that meet the repeatability criteria."
          : description === USABLE
            ? translationText.oscillometryIsUsableBecause || "R5 CoV is between 10% and 15% and the session has at least 3 acceptable manoeuvres."
            : description === NOT_ACCEPTABLE
              ? translationText.oscillometryIsNotAcceptableBecause || "A session that has less than 3 acceptable manoeuvres or has at least 3 acceptable manoeuvres but shows errors/does not meet the repeatability criteria (reproducibility of R5 > 15%)."
              : translationText.notEnoughInfo || "Not Enough Information"}
      </Popover.Content>
    </Popover>
  );

  const renderAcceptabilityCount = (bestMeasurements: GroupedOscillometryMeasurement['bestMeasurements']): string => {
    if (bestMeasurements?.numAcceptedWfms !== undefined && bestMeasurements?.datasetCount !== undefined) {
      return `${bestMeasurements?.numAcceptedWfms} / ${bestMeasurements?.datasetCount}`
    };
    return `${waveforms.filter(w => w.rejected !== undefined && !w.rejected).length} / ${waveforms.length}`;
  };

  return (
    <Container fluid className="PulmonaryFunctionTestingView px-0">
      <Row className="d-flex flex-row justify-content-center">
        {/* Left Column - Tables */}
        <Col className="mb-4">
          {/* Best Measurements Table */}
          <Row className="pl-3">
            <Container className="statsTitle px-0">
              {translationText.bestMeasurements || 'Best Measurements'}
            </Container>
            <Container className="table-stats">
              <Table size="sm" className="pb-0 mb-0" borderless>
                <thead>
                  <tr className="border-bottom">
                    <th><i>{bestMeasurements?.gdtSource?.referenceName ?? 'Oostveen 2013'}</i></th>
                    <th>{translationText.best || 'Best'}</th>
                    <th>{translationText.predicted || 'Predicted'}</th>
                    <th>{translationText.percentPredicted || '% Pred'}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>R5 ({bestMeasurements?.R5?.unit})</td>
                    <td>{bestMeasurements?.R5?.value?.toFixed(2) ?? '--'}</td>
                    <td>{bestMeasurements?.R5?.reference?.toFixed(2) ?? '--'}</td>
                    <td>{bestMeasurements?.R5?.percentPredicted?.toFixed(0) ?? '--'}%</td>
                  </tr>
                  <tr>
                    <td>R5-20 ({bestMeasurements?.R5_20?.unit})</td>
                    <td>{bestMeasurements?.R5_20?.value?.toFixed(2) ?? '--'}</td>
                    <td>{bestMeasurements?.R5_20?.reference?.toFixed(2) ?? '--'}</td>
                    <td>{bestMeasurements?.R5_20?.percentPredicted?.toFixed(0) ?? '--'}%</td>
                  </tr>
                  <tr>
                    <td>AX ({bestMeasurements?.AX?.unit})</td>
                    <td>{bestMeasurements?.AX?.value?.toFixed(2) ?? '--'}</td>
                    <td>{bestMeasurements?.AX?.reference?.toFixed(2) ?? '--'}</td>
                    <td>{bestMeasurements?.AX?.percentPredicted?.toFixed(0) ?? '--'}%</td>
                  </tr>
                  <tr>
                    <td>VT ({bestMeasurements?.VT?.unit})</td>
                    <td>{bestMeasurements?.VT?.value?.toFixed(2) ?? '--'}</td>
                    <td>{bestMeasurements?.VT?.reference?.toFixed(2) ?? '--'}</td>
                    <td>{bestMeasurements?.VT?.percentPredicted?.toFixed(0) ?? '--'}%</td>
                  </tr>
                  <tr>
                    <td>COH5</td>
                    <td>{bestMeasurements?.COH5?.value?.toFixed(2) ?? '--'}</td>
                    <td>{bestMeasurements?.COH5?.reference?.toFixed(2) ?? '--'}</td>
                    <td>{bestMeasurements?.COH5?.percentPredicted?.toFixed(0) ?? '--'}%</td>
                  </tr>
                  <tr>
                    <td>Fres ({bestMeasurements?.Fres?.unit})</td>
                    <td>{bestMeasurements?.Fres?.value?.toFixed(2) ?? '--'}</td>
                    <td>{bestMeasurements?.Fres?.reference?.toFixed(2) ?? '--'}</td>
                    <td>{bestMeasurements?.Fres?.percentPredicted?.toFixed(0) ?? '--'}%</td>
                  </tr>
                  <tr>
                    <td>Respiratory Rate ({bestMeasurements?.respiratoryRate?.unit})</td>
                    <td>{bestMeasurements?.respiratoryRate?.value?.toFixed(2) ?? '--'}</td>
                    <td>{bestMeasurements?.respiratoryRate?.reference?.toFixed(2) ?? '--'}</td>
                    <td>{bestMeasurements?.respiratoryRate?.percentPredicted?.toFixed(0) ?? '--'}%</td>
                  </tr>
                  <tr>
                    <td>{'ERS 2020'}</td>
                    <td>
                      <OverlayTrigger
                        trigger={['hover', 'focus']}
                        placement="right"
                        delay={{ show: 250, hide: 1000 }}
                        overlay={acceptablePopover(acceptabilityStatus)}
                      >
                        <span>
                          <Ats2019AcceptabilityIcon description={acceptabilityStatus} />
                        </span>
                      </OverlayTrigger>
                    </td>
                    <td colSpan={3} />
                  </tr>
                </tbody>
              </Table>
            </Container>
          </Row>

          {/* Session Quality Table */}
          <Row className="pl-3 mt-2">
            <Container className="statsTitle px-0">
              {translationText.sessionQuality || 'Session Quality'}
            </Container>
            <Container className="table-stats">
              <Table size="sm" className="pb-0 mb-0" borderless>
                <thead>
                  <tr className="border-bottom">
                    <th><i>ERS 2020</i></th>
                    <th>{translationText.value || 'Value'}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>R5 SD (cmH2O.s/L)</td>
                    <td>{bestMeasurements?.R5?.sd?.toFixed(2) ?? '--'}</td>
                  </tr>
                  <tr>
                    <td>R5 CV (%)</td>
                    <td>{bestMeasurements?.R5?.cv?.toFixed(2) ?? '--'}</td>
                  </tr>
                  <tr>
                    <td>{translationText.repeatable || 'Repeatable'}</td>
                    <td>{getRepeatabilityStatus()}</td>
                  </tr>
                  <tr>
                    <td>{translationText.acceptableEfforts || 'Acceptable Efforts'}</td>
                    <td>{renderAcceptabilityCount(bestMeasurements)}</td>
                  </tr>
                </tbody>
              </Table>
            </Container>
          </Row>

          {/* Session Demographics */}
          <Row className="pl-3 pt-2">
            <Container className="statsTitle px-0">
              {translationText.sessionDemographics || 'Session Demographics'}
            </Container>
            <TrialsDemographicsTable
              demographics={sessionDemographics}
              useMetric={authenticatedProvider.useMetric}
              dateFormat={authenticatedProvider.dateFormat}
              disableEthnicity={organization?.featureSet?.disableEthnicity}
              birthdateDisplay={organization?.featureSet?.birthdateDisplay}
              hideWeightDemographics={program?.featureSet?.hideWeightDemographics}
              ethnicityReferenceEquationLabels={program?.featureSet?.ethnicityReferenceEquationLabels}
            />
          </Row>

          {/* Visit Labels (if enabled) */}
          {shouldShowVisitLabels && bestMeasurements && !isReadOnly && (
            <Row className="pl-3 pt-2">
              <Container className="statsTitle px-0">
                {translationText.visitLabels || 'Visit Labels'}
              </Container>
              <Container>
                <div style={{ fontSize: 14 }}>
                  <MeasurementTrialLabelsDropdown
                    organization={organization}
                    measurement={bestMeasurements}
                    updateLocalMeasurement={refetchMeasurements}
                    reloadParticipantDetailsAtHomeStartDate={async () => { }}
                  />
                </div>
              </Container>
            </Row>
          )}

          <OscillometryNotes
            patientID={participantID}
            bestMeasurements={bestMeasurements}
            refetchMeasurements={refetchMeasurements}
          />
        </Col>

        {/* Middle Column - Charts */}
        <Col className="PulmonaryFunctionTestingView-Second-Col mb-4">
          {/* Resistance vs Frequency Chart */}
          <Row className="pl-4">
            <Container className="statsTitle pl-0">
              <span>
                {translationText.bestFrequencySpectrum || 'Best Frequency Spectrum'}
                {!!bestMeasurements?.rejected && <span className='text-danger'> - {translationText.rejected || 'Rejected'}</span>}
              </span>
            </Container>
            <Container className="volume-chart" style={{ cursor: 'pointer' }} onClick={() => setShowModal(true)} title={translationText.clickToEnlarge || 'Click to enlarge'}>
              <FrequencyCurveChart frequencyCurve={bestMeasurements?.frequencyCurve} rejected={!!bestMeasurements?.rejected} width={300} showLegend hideTooltip />
            </Container>
          </Row>

          {/* Z-Score Chart */}
          <Row className="pl-4">
            <Container className="statsTitle pl-0">
              {translationText.zScoreCharts || 'Z-Score Charts'}
            </Container>
            <OscillometryZScoreChart measurements={bestMeasurements} />
          </Row>
        </Col>

        {/* Right Column - Waveform Cards */}
        <Col className="PulmonaryFunctionTestingView-Third-Col">
          <Container className="statsTitle pl-0">
            {translationText.measurementsInSession || 'Measurements in Session'}
          </Container>
          <Container className="EffortContainer-Efforts">
            {waveforms.length > 0 ? (
              waveforms.map((waveform, index) => (
                <OscillometryWaveformCard
                  key={waveform.measurementID}
                  waveform={waveform}
                  waveformNum={waveforms.length - index}
                  bestMeasurements={bestMeasurements}
                  toggleRejected={toggleRejected}
                  saveAcceptabilityCriteria={saveAcceptabilityCriteria}
                  isReadOnly={isReadOnly}
                  seriesEditing={seriesEditing}
                />
              ))
            ) : (
              <Container className="text-center text-muted py-4">
                {translationText.noMeasurementsFound || 'No measurements found'}
              </Container>
            )}
          </Container>

          {/* Device Info */}
          <p className="text-center mt-2">
            {translationText.deviceSerial || 'Device Serial'}: <b>{bestMeasurements?.deviceSerial || 'N/A'}</b>
          </p>

          {/* Calibration Section (if available) */}
          {groupedMeasurement.calibration.length > 0 && (
            <Row className="mt-3">
              <Col>
                <Container className="statsTitle px-0">
                  {translationText.calibration || 'Calibration'}
                </Container>
                <Container className="table-stats">
                  <Table size="sm" responsive className="pb-0 mb-0" borderless>
                    <thead>
                      <tr className='border-bottom'>
                        <th>{translationText.time || 'Time'}</th>
                        <th>{translationText.impedanceCheck || 'Impedance'}</th>
                        <th>{translationText.frequencySweepCheck || 'Frequency Sweep'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedMeasurement.calibration.map((cal) => (
                        <tr key={cal.measurementID}>
                          <td>{moment.parseZone(cal.roundtripTime).format('h:mm:ss a')}</td>
                          <td>{cal.calibration?.impedanceCheck ?? '--'}</td>
                          <td>{cal.calibration?.frequencySweepCheck ?? '--'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Container>
              </Col>
            </Row>
          )}
        </Col>
      </Row>

      {/* Modal */}
      <FrequencyCurveModal
        show={showModal}
        onHide={() => setShowModal(false)}
        frequencyCurve={bestMeasurements?.frequencyCurve}
        title={`${translationText.bestMeasurements || 'Best Measurements'} - ${translationText.frequencyCurve || 'Frequency Curve'}`}
        rejected={!!bestMeasurements?.rejected}
      />

    </Container>
  );
};